import React, { FC, MutableRefObject, ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { fromEvent, Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import useIsMountedRef from 'use-is-mounted-ref'
import ReportList from '../../../../../React/MainMenu/ReportList'
import { ReactState } from '../../../../../React/Common/Types/Types'
import { ReportListItemsIndexByReportID, ReportListItemType } from '../../../../../React/MainMenu/ReportList/Types'
import { getSelectedReport, handleRptPortalReportItemSelection } from '../../../../../NonReact/utils/RptPortal'
import { displayError, displayInfoMessage } from '../../../../../React/Common/Utils/Messages'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { FOLDERS_SEPARATOR_BACKEND } from '../../../../../React/MainMenu/ReportList/Constants'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { ServletPathContextProvider } from '../../../../../React/Common/Contexts/ServletPathContext'
import { BaseContexts } from '../../../../../React/Common/BaseContexts'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { reportingMenuTrigger$ } from '../../../../../NonReact/common/RxJS/ReportingMenuTriggerState'
import IconButton, { IconButtonType } from '../../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../../React/Common/Components/Icon'
import { usePersistentState } from '../../../../../React/Common/Hooks/usePersistState'
import { mainMenuTrigger$ } from '../../../../../NonReact/common/RxJS/MainMenuTriggerState'
import {
    pauseReportXMLContentUpdates,
    resumeReportXMLContentUpdates
} from '../../../../../NonReact/utils/ContentUpdateUtils'
import { reportListUpdate$ } from '../../../../../NonReact/common/RxJS/ReportListState'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { ReportingMenuLanguage } from '../../../../../../i18n'
import {
    DEFAULT_REPORTING_WIDTH,
    REPORTING_MIN_WIDTH,
    SIDEBAR_PINNED_STORAGE_KEY,
    SIDEBAR_WIDTH_COLLAPSED,
    SIDEBAR_WIDTH_EXPANDED
} from '../../../../../NonReact/constants/ReactRouter/Header/Constants'
import { ReportListItemResponse, ReportListResponse, ReportListState } from '../../../../../NonReact/common/Types/Types'
import { retrieveReportList } from '../../../../../NonReact/common/API/Reporting'

const DELETED_PREFIX: string = 'DELETED_'
const FIRST_REPORTING_VIEW_KEY: string = 'isFirstReportingView'

export const ReportingMenu: FC = (): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ReportingMenuLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [reportListItemsAsMap, setReportListItemsAsMap]: ReactState<ReportListItemsIndexByReportID> =
        useState<ReportListItemsIndexByReportID>(new Map())
    // Currently hovered item ID.
    const [hoveredItemID, setHoveredItemID]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)
    // Currently editing item ID. (Allowed only one item to be edited at the same time).
    const [editingItemID, setEditingItemID]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)
    // Currently checked (expanded) items IDs. (Allowed multiple items to be checked / expanded at the same time).
    const [checkedItemsIDs, setCheckedItemsIDs]: ReactState<string[]> = useState<string[]>([])
    // Currently checked (expanded) items IDs within recycle bin folder. (Allowed multiple items to be checked / expanded at the same time).
    const [selectedReportBinItemIDs, setSelectedReportBinItemIDs] = useState<string[]>([])

    const [isDragging, setIsDragging]: ReactState<boolean> = useState<boolean>(false)

    const [currentWidth, setCurrentWidth]: ReactState<number> = usePersistentState<number>(
        'reportingMenuWidth',
        DEFAULT_REPORTING_WIDTH
    )
    const [isPinned, setIsPinned]: ReactState<boolean> = usePersistentState<boolean>('reportingMenuPinned', false)
    const [isReportingFirstView, setIsReportingFirstView]: ReactState<boolean> = usePersistentState<boolean>(
        FIRST_REPORTING_VIEW_KEY,
        true
    )
    const [isReportingMenuOpen]: RxJSCurrentState<boolean> = useRxJSState<boolean>(
        reportingMenuTrigger$,
        isPinned || isReportingFirstView
    )

    const [isSidebarPinned]: ReactState<boolean> = usePersistentState<boolean>(SIDEBAR_PINNED_STORAGE_KEY, false)
    const [isSidebarExpanded]: RxJSCurrentState<boolean> = useRxJSState<boolean>(mainMenuTrigger$, isSidebarPinned)

    const [reportListState]: RxJSCurrentState<ReportListState> = useRxJSState<ReportListState>(reportListUpdate$)

    const [maxWidth, setMaxWidth]: ReactState<number> = useState<number>(window.innerWidth / 2)

    /**
     * Converts list of report list items with no hierarchy to list of items with links to parent item using its
     * identifier.
     *
     * @param reportsFromResponse - reports to render.
     */
    const toReportListItemsAsMap = (reportsFromResponse: ReportListItemResponse[]): ReportListItemsIndexByReportID =>
        reportsFromResponse.reduce(
            (
                reportListItemsMapByID: ReportListItemsIndexByReportID,
                report: ReportListItemResponse
            ): ReportListItemsIndexByReportID => {
                const description: string = report.Description
                const filePath: string[] = description.split(FOLDERS_SEPARATOR_BACKEND)

                filePath.reduce((accum: string, next: string, index: number): string => {
                    const parentPath: Definable<string> = isStringNotEmpty(accum) ? accum : undefined
                    const newPath: string = `${accum}${index > 0 ? FOLDERS_SEPARATOR_BACKEND : ''}${next}`

                    if (index === filePath.length - 1) {
                        const reportName: string = next

                        let parentFolderID: Definable<string>

                        if (report.IsBinned) {
                            if (filePath.length === 1 || IsVarEmpty(parentPath)) {
                                parentFolderID = undefined
                            } else {
                                parentFolderID = `${DELETED_PREFIX}${parentPath}`
                            }
                        } else {
                            parentFolderID = filePath.length === 1 ? undefined : parentPath
                        }

                        reportListItemsMapByID.set(report.ReportID, {
                            isReport: true,
                            id: report.ReportID,
                            name: reportName,
                            duration: report.Duration,
                            rptType: report.ReportType,
                            groupingViewType: report.GroupingViewType,
                            supportsIconViews: report.SupportsIconsViews,
                            supportsCharting: report.SupportsCharting,
                            supportsRowHighlighting: report.SupportsRowHighlighting,
                            viewType: report.ViewType,
                            WBTileType: report.WBTileType,
                            filterDevices: report.FilterDevices,
                            filterDevicesMax: report.FilterDevicesMax,
                            userLicensedFor: report.UserLicensedFor,
                            isLicensed: report.IsLicensed,
                            isLicensedRealTime: report.IsLicensedRealTime,
                            isScheduleActive: report.IsScheduleActive,
                            zoomLevel: report.ZoomLevel,
                            isAutoSwitched: report.IsAutoSwitched,
                            autoSwitchIntervalDisplay: report.AutoSwitchIntervalDisplay,
                            durationFilterSummary: report.DurationFilterSummary,
                            parentFolderID,
                            description,
                            isBinned: report.IsBinned,
                            daysUntilDeletion: report.DaysUntilDeletion
                        })
                    } else {
                        const folderID: string = report.IsBinned ? `${DELETED_PREFIX}${newPath}` : newPath
                        let parentFolderID: Definable<string>

                        if (report.IsBinned) {
                            if (filePath.length === 1 || IsVarEmpty(parentPath)) {
                                parentFolderID = undefined
                            } else {
                                parentFolderID = `${DELETED_PREFIX}${parentPath}`
                            }
                        } else if (filePath.length === 1) {
                            parentFolderID = undefined
                        } else {
                            parentFolderID = parentPath
                        }

                        reportListItemsMapByID.set(folderID, {
                            isReport: false,
                            id: folderID,
                            name: next,
                            parentFolderID,
                            description: newPath,
                            isBinned: report.IsBinned,
                            daysUntilDeletion: report.IsBinned ? report.DaysUntilDeletion : -1
                        })
                    }

                    return newPath
                }, EMPTY_STRING)

                return reportListItemsMapByID
            },
            new Map()
        )

    /**
     * Updates selected report list items after a new report is added or the existing one is updated.
     *
     * @param reportID - Report ID.
     * @param reportDesc - Whole report description with folders.
     * @param newReportListItemsAsMap - The map of report list items by ID.
     */
    const updateSelectedReportListItemsAfterAddOrUpdate = (
        reportID: string,
        reportDesc: string,
        newReportListItemsAsMap: ReportListItemsIndexByReportID
    ): void => {
        // Expand all related folders.
        const rptDescParts: string[] = reportDesc.split(FOLDERS_SEPARATOR_BACKEND)

        const foldersToExpand: string[] = rptDescParts.reduce((acc: string[], _: string, index: number): string[] => {
            if (index < rptDescParts.length - 1) {
                const folderPathCopy: string[] = rptDescParts.slice(0, rptDescParts.length - 1 - index)

                acc.push(folderPathCopy.join(FOLDERS_SEPARATOR_BACKEND))
            }

            return acc
        }, [])

        // Based on the ID select the report that was updated and let the all previously open folders.
        setCheckedItemsIDs((prevCheckedItemIDs: string[]): string[] => [
            ...prevCheckedItemIDs.filter(
                (rptListItemID: string): boolean => !newReportListItemsAsMap.get(rptListItemID)?.isReport
            ),
            ...foldersToExpand.filter((folderID: string): boolean => prevCheckedItemIDs.indexOf(folderID) === -1),
            reportID
        ])
    }

    /**
     * Initialises report list component data.
     */
    const initialiseReportListData = (): Promise<void> => {
        if (userInfo && isMounted.current) {
            return retrieveReportList()
                .then((reportListResponse: Definable<ReportListResponse>): void => {
                    // Process report list.
                    if (isVarNotEmpty(reportListResponse)) {
                        const newReportListItemsAsMap: ReportListItemsIndexByReportID = toReportListItemsAsMap(
                            reportListResponse.ReportList
                        )

                        setReportListItemsAsMap(newReportListItemsAsMap)

                        // Remove items from checked items if they are no longer returned in report list.
                        setCheckedItemsIDs((prevCheckedItemsIDs: string[]): string[] =>
                            prevCheckedItemsIDs.filter((rptListItemID: string): boolean => {
                                const reportListItem: Definable<ReportListItemType> =
                                    newReportListItemsAsMap.get(rptListItemID)

                                return isVarNotEmpty(reportListItem) && !reportListItem.isBinned
                            })
                        )
                        setSelectedReportBinItemIDs((prevCheckedItemsIDs: string[]): string[] =>
                            prevCheckedItemsIDs.filter((rptListItemID: string): boolean => {
                                const reportListItem: Definable<ReportListItemType> =
                                    newReportListItemsAsMap.get(rptListItemID)

                                return isVarNotEmpty(reportListItem) && reportListItem.isBinned
                            })
                        )
                    }
                })
                .catch(displayError)
        }

        return Promise.resolve()
    }

    /**
     * Initialises report list with new data and starts the report specified if it is present new data.
     *
     * @param reportID ID of the report to find in new data and start.
     * @param reportDesc - Description of the report
     */
    const refreshReportListDataAndStartReport = (reportID: string, reportDesc: string): Promise<void> =>
        retrieveReportList()
            .then((reportListResponse: Definable<ReportListResponse>): void => {
                // Process report list.
                const newReportListItemsAsMap: ReportListItemsIndexByReportID = toReportListItemsAsMap(
                    reportListResponse?.ReportList ?? []
                )
                const reportToStart: Nullable<ReportListItemType> = newReportListItemsAsMap.get(reportID)

                if (isVarNotEmpty(reportToStart)) {
                    handleRptPortalReportItemSelection(reportToStart)
                }

                setReportListItemsAsMap(newReportListItemsAsMap)

                updateSelectedReportListItemsAfterAddOrUpdate(reportID, reportDesc, newReportListItemsAsMap)
            })
            .catch(displayError)

    const mouseMoveHandler = useCallback(
        (event: MouseEvent): void => {
            event.preventDefault()
            const newWidth: number =
                event.clientX -
                document.body.offsetLeft -
                (isSidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED)

            const isOverMaxWidth: boolean = newWidth > maxWidth
            const isUnderMinWidth: boolean = newWidth < REPORTING_MIN_WIDTH

            if (isOverMaxWidth) {
                return setCurrentWidth(maxWidth)
            }

            if (isUnderMinWidth) {
                return setCurrentWidth(REPORTING_MIN_WIDTH)
            }

            return setCurrentWidth(newWidth)
        },
        [isSidebarExpanded, maxWidth]
    )
    const mouseDownHandler = useCallback((event: React.MouseEvent): void => {
        event.preventDefault()
        setIsDragging(true)
    }, [])
    const mouseUpHandler = useCallback((): void => {
        setIsDragging(false)
        getSelectedReport()?.AutoSizeContent(true)
    }, [])

    const mouseMoveSubscription: MutableRefObject<Definable<Subscription>> = useRef<Definable<Subscription>>()
    const mouseUpSubscription: MutableRefObject<Definable<Subscription>> = useRef<Definable<Subscription>>()

    const deleteResources = (): void => {
        mouseMoveSubscription.current?.unsubscribe()
        mouseUpSubscription.current?.unsubscribe()
    }

    useEffect((): (() => void) => {
        deleteResources()
        resumeReportXMLContentUpdates()

        if (isDragging) {
            pauseReportXMLContentUpdates()
            mouseMoveSubscription.current = fromEvent(window, 'mousemove').subscribe((event: Event): void =>
                mouseMoveHandler(event as MouseEvent)
            )
            mouseUpSubscription.current = fromEvent(window, 'mouseup').subscribe(mouseUpHandler)
        }

        return deleteResources
    }, [isDragging, isSidebarExpanded, mouseMoveHandler, mouseDownHandler, mouseUpHandler, maxWidth])

    useEffect((): void => {
        initialiseReportListData()
    }, [userInfo])

    useEffect((): (() => void) => {
        fromEvent(window, 'resize')
            .pipe(debounceTime(10))
            .subscribe((): void => setMaxWidth(window.innerWidth / 2))

        return deleteResources
    }, [])

    useEffect((): void => {
        try {
            if (reportListState && !reportListState?.isLoading) {
                const { ReportID, ReportDesc, ReportList: rlsReportList }: ReportListState = reportListState

                if (rlsReportList) {
                    // Process report list.
                    const newReportListItemsAsMap: ReportListItemsIndexByReportID =
                        toReportListItemsAsMap(rlsReportList)

                    setReportListItemsAsMap(newReportListItemsAsMap)
                    updateSelectedReportListItemsAfterAddOrUpdate(
                        ReportID ?? EMPTY_STRING,
                        ReportDesc ?? EMPTY_STRING,
                        newReportListItemsAsMap
                    )
                }
            }
        } catch (error) {
            displayError(error as Error)
        }
    }, [reportListState])

    useEffect((): void => {
        setTimeout((): void => {
            getSelectedReport()?.AutoSizeContent?.(true)
        }, 300)
    }, [isReportingMenuOpen])

    return (
        <BaseContexts>
            <ServletPathContextProvider>
                <div
                    className={`reporting-list-container ${isDragging ? 'rlc-dragging' : EMPTY_STRING}`}
                    style={{ width: isReportingMenuOpen ? `${currentWidth}px` : '0' }}
                    aria-label='Reporting list container please click "Reporting" menu option to view'
                >
                    <div className='rlc-header'>
                        <IconButton
                            iconName={isPinned ? IconName.PINNED : IconName.PIN}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => setIsPinned((currentlyPinned: boolean): boolean => !currentlyPinned)}
                        />
                        <IconButton
                            iconName={IconName.X}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => {
                                reportingMenuTrigger$.announce(false)

                                if (isReportingFirstView) {
                                    setIsReportingFirstView(false)
                                    displayInfoMessage(
                                        language.get(ReportingMenuLanguage.ReportingMenu_FirstClosePopup)
                                    )
                                }
                            }}
                        />
                    </div>
                    <ReportList
                        reportListItemsAsMap={reportListItemsAsMap}
                        setReportListItemsAsMap={setReportListItemsAsMap}
                        hoveredItemID={hoveredItemID}
                        setHoveredItemID={setHoveredItemID}
                        editingItemID={editingItemID}
                        setEditingItemID={setEditingItemID}
                        checkedItemsIDs={checkedItemsIDs}
                        setCheckedItemsIDs={setCheckedItemsIDs}
                        selectedReportBinItemIDs={selectedReportBinItemIDs}
                        setSelectedReportBinItemIDs={setSelectedReportBinItemIDs}
                        initialiseReportListData={initialiseReportListData}
                        refreshReportListDataAndStartReport={refreshReportListDataAndStartReport}
                    />
                    <div
                        className='rlc-dragger'
                        onMouseDown={mouseDownHandler}
                        onDoubleClick={(): void => setCurrentWidth(DEFAULT_REPORTING_WIDTH)}
                    ></div>
                </div>
            </ServletPathContextProvider>
        </BaseContexts>
    )
}
